import React, { useState, useEffect, useRef } from 'react';
import { nfConfirm } from '../components/confirm';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import SubscriptionCatalogTab from '../components/SubscriptionCatalogTab';
import PhotoLightbox from '../components/PhotoLightbox';
import Toggle from '../components/Toggle';
import Tooltip from '../components/Tooltip';
import Select from '../components/Select';
import PackagingPage from './PackagingPage';
import BouquetsPage from './BouquetsPage';
import CategoriesTab from '../components/CategoriesTab';
import { normProductName } from '../utils/productName';
import { openStaffStream } from '../utils/ordersStream';

// Убираем /api из URL для загрузки изображений
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '');
const PRODUCTS_PER_PAGE = 500;
const STEP_PRESETS = [1, 5, 10, 20];  // шаг продажи — чипы; иное значение вводится в поле «свой»

// Дефолтные названия разделов витрины (фолбэк, совпадает с бэком)
const DEFAULT_CATALOG_LABELS = { products: 'Стебли', bouquets: 'Букеты', subscription: 'Подписка' };

function ProductsPage({ showToast }) {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [activeTab, setActiveTab] = useState('products'); // products | bouquets | subscriptions | packaging | categories
  // Названия разделов на витрине; переименовывает только владелец (карандашик)
  const [catalogLabels, setCatalogLabels] = useState(DEFAULT_CATALOG_LABELS);
  const [labelEditor, setLabelEditor] = useState(null); // { key: 'products'|'bouquets'|'subscription', value }
  const [subscriptionTabAction, setSubscriptionTabAction] = useState(null);
  const [packagingCreateTrigger, setPackagingCreateTrigger] = useState(null);
  const [bouquetsCreateTrigger, setBouquetsCreateTrigger] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [pendingImages, setPendingImages] = useState([]); // Файлы для загрузки при создании
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    description: '',
    price: '',
    quantity: '0',
    step: '1',
    category_id: '',
    is_active: true,
    is_hidden: false,
    shelf_life_days: '',
    height_cm: '',
    flower: ''
  });
  // Поле «Цветок»: подсказка видов и угаданный по названию; flowerAuto — в поле угаданное,
  // админ его не трогал (меняется вместе с названием)
  const [flowerHint, setFlowerHint] = useState({ flowers: [], guess: null });
  const [flowerAuto, setFlowerAuto] = useState(true);
  const [sortField, setSortField] = useState(null); // 'price' | 'quantity' | 'category' | 'hidden'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [searchName, setSearchName] = useState('');
  const [archivedMode, setArchivedMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images: [{src}], index } - снимок из таблицы
  const [modalLightboxIndex, setModalLightboxIndex] = useState(null); // индекс в editingProduct.images
  const [namePool, setNamePool] = useState([]); // товары (активные + архив) — подсказки и запрет дублей при создании
  const [nameFocused, setNameFocused] = useState(false);
  // id карточки, которую только что вернули из архива из окна «Новый товар» — плашка в ней
  const [restoredId, setRestoredId] = useState(null);
  const [searchInHeader, setSearchInHeader] = useState(false);
  const fileInputRef = useRef(null);
  const flowwowFileInputRef = useRef(null);
  // «Свой» шаг продажи — отдельно от чипов: пока значение набирается («1» → «15»),
  // чип «1» не должен перехватывать поле
  const [customStep, setCustomStep] = useState('');

  useEffect(() => {
    loadData(archivedMode);
  }, [archivedMode]);

  // «Цветок»: пока поле не трогали руками — вид по названию (с паузой, пока название набирают)
  useEffect(() => {
    if (!showModal) return undefined;
    const t = setTimeout(() => {
      api.getProductFlowers(formData.name)
        .then((h) => {
          setFlowerHint({ flowers: h.flowers || [], guess: h.guess || null });
          if (flowerAuto) setFormData(prev => ({ ...prev, flower: h.guess || '' }));
        })
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, formData.name]);

  // Названия разделов витрины из настроек; подключён ли Flowwow (тогда высота стебля обязательна)
  const [flowwowEnabled, setFlowwowEnabled] = useState(false);
  useEffect(() => {
    api.getSettings()
      .then((s) => {
        setCatalogLabels({
          products: s.catalog_label_products || DEFAULT_CATALOG_LABELS.products,
          bouquets: s.catalog_label_bouquets || DEFAULT_CATALOG_LABELS.bouquets,
          subscription: s.catalog_label_subscription || DEFAULT_CATALOG_LABELS.subscription,
        });
        setFlowwowEnabled(!!s.flowwow_enabled);
      })
      .catch(() => {});
  }, []);

  const saveCatalogLabel = async () => {
    if (!labelEditor) return;
    const value = (labelEditor.value || '').trim();
    if (!value) {
      showToast('Название не может быть пустым', 'error');
      return;
    }
    try {
      await api.updateCatalogLabels({ [`catalog_label_${labelEditor.key}`]: value });
      setCatalogLabels((prev) => ({ ...prev, [labelEditor.key]: value }));
      setLabelEditor(null);
      showToast('Название раздела обновлено — на витрине сразу');
    } catch (e) {
      showToast(e.message || 'Ошибка сохранения названия', 'error');
    }
  };

  // Карандашик владельца: переименование раздела витрины
  const renderLabelPencil = (key) => (
    isOwner ? (
      <span
        title={`Название на витрине: «${catalogLabels[key]}». Нажмите, чтобы переименовать`}
        onClick={(e) => { e.stopPropagation(); setLabelEditor({ key, value: catalogLabels[key] }); }}
        style={{ marginLeft: 2, cursor: 'pointer', opacity: 0.55, display: 'inline-flex' }}
      >
        {/* Карандаш — SVG, а не эмодзи (UX-аудит 25.10) */}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </svg>
      </span>
    ) : null
  );

  // SSE: обновление остатков в реальном времени при создании заказа
  useEffect(() => {
    // Остатки — только сотрудникам: пропуск и переподключение — в openStaffStream
    const es = openStaffStream('/products/stock-stream');
    es.onmessage = (event) => {
      try {
        const updates = JSON.parse(event.data);
        setProducts(prev => prev.map(p => {
          const upd = updates.find(u => u.id === p.id);
          return upd ? { ...p, quantity: upd.quantity, is_hidden: upd.is_hidden } : p;
        }));
      } catch (e) {
        console.error('SSE parse error:', e);
      }
    };
    return () => es.close();
  }, []);


  const loadData = async (archived = false, { silent = false } = {}) => {
    // silent — обновляем данные без полноэкранного спиннера, чтобы сохранить scroll и фокус
    if (!silent) setIsLoading(true);
    try {
      const [productsResponse, categoriesData] = await Promise.all([
        api.getProducts(1, PRODUCTS_PER_PAGE, archived),
        api.getCategories(),
      ]);

      const productsList = productsResponse.items || productsResponse;
      setProducts(productsList);
      setTotalProducts(productsResponse.total || productsList.length);
      setCategories(categoriesData);
    } catch (error) {
      showToast('Ошибка загрузки данных', 'error');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleArchive = (product) => {
    const toArchive = product.is_active !== false;
    if (toArchive) {
      setConfirmDialog({
        title: 'Перевести в архив?',
        message: `Товар «${product.name}» будет скрыт из каталога и матрицы покрытия. Вы сможете восстановить его в любой момент из раздела Архив.`,
        confirmText: 'В архив',
        variant: 'danger',
        onConfirm: () => doArchive(product, false),
      });
    } else {
      setConfirmDialog({
        title: 'Восстановить из архива?',
        message: `Товар «${product.name}» вернётся в список активных. Он останется скрытым из каталога до появления остатка на складе.`,
        confirmText: 'Восстановить',
        variant: 'success',
        onConfirm: () => doArchive(product, true),
      });
    }
  };

  const doArchive = async (product, isActive) => {
    setConfirmDialog(null);
    try {
      await api.updateProduct(product.id, { is_active: isActive });
      setProducts(prev => prev.filter(p => p.id !== product.id));
      setTotalProducts(prev => prev - 1);
      showToast(isActive ? 'Товар восстановлен из архива' : 'Товар перемещён в архив');
    } catch (error) {
      showToast('Ошибка при изменении статуса товара', 'error');
    }
  };


  // Из окна создания: такое название уже есть в архиве — предлагаем вернуть ту карточку,
  // а не заводить новую (создать с тем же названием всё равно нельзя)
  const offerRestoreFromCreate = (product) => {
    setConfirmDialog({
      title: 'Восстановить из архива?',
      message: `«${product.name}» уже есть в архиве. Вернуть эту карточку вместо создания новой? Она останется скрытой из каталога до появления остатка на складе — откроется для правки.`,
      confirmText: 'Восстановить',
      variant: 'success',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.updateProduct(product.id, { is_active: true });
          showToast('Товар восстановлен из архива');
          setShowModal(false);
          await loadData(archivedMode, { silent: true });
          openEditModal({ ...product, is_active: true });
          setRestoredId(product.id);
        } catch (error) {
          showToast(error?.message || 'Не удалось восстановить товар', 'error');
        }
      },
    });
  };

  const openCreateModal = async () => {
    setEditingProduct(null);
    setPendingImages([]);
    setFormData({
      name: '',
      short_name: '',
      description: '',
      price: '',
      quantity: '0',
      step: '1',
      category_id: '',
      is_active: true,
      is_hidden: true,  // новый товар без склада — скрыт по умолчанию
      height_cm: '',
      flower: ''
    });
    setFlowerAuto(true);
    setCustomStep('');
    setShowModal(true);
    // Загружаем имена всех товаров (активные + архив) для подсказок
    try {
      const [activeResp, archivedResp] = await Promise.all([
        api.getProducts(1, PRODUCTS_PER_PAGE, false),
        api.getProducts(1, PRODUCTS_PER_PAGE, true),
      ]);
      // Целиком товары: из подсказки можно открыть карточку или восстановить её из архива
      const activeList = (activeResp.items || activeResp).map(p => ({ ...p, is_active: true }));
      const archivedList = (archivedResp.items || archivedResp).map(p => ({ ...p, is_active: false }));
      setNamePool([...activeList, ...archivedList]);
    } catch (e) {
      // Не критично — подсказки просто не покажутся
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setPendingImages([]);
    setRestoredId(null);  // плашку «восстановлена» ставит только восстановление из окна создания
    setFormData({
      name: product.name,
      short_name: product.short_name || '',
      description: product.description || '',
      price: product.price.toString(),
      quantity: (product.quantity || 0).toString(),
      step: (product.step || 1).toString(),
      category_id: product.category?.id?.toString() || '',
      is_active: product.is_active !== false,
      is_hidden: product.is_hidden === true,
      shelf_life_days: product.shelf_life_days != null ? product.shelf_life_days.toString() : '',
      stock_arrival_date: product.stock_arrival_date || '',
      height_cm: product.height_cm != null ? product.height_cm.toString() : '',
      flower: product.flower || ''
    });
    setFlowerAuto(!product.flower_manual);
    setCustomStep(STEP_PRESETS.includes(product.step || 1) ? '' : String(product.step));
    setShowModal(true);
  };

  // Текст про плановые поставки для «показать нельзя»: называет просроченную
  // накладную и что с ней делать. Раньше тост сообщал «плановых поставок нет»,
  // хотя накладная в складе есть — просто ждала вчера и не принята, а такие
  // с 14.09 в доступность не входят.
  const plannedStatusLine = (p, maxPlanned, step) => {
    const overdue = p?.overdue_planned || [];
    if (overdue.length) {
      const o = overdue[0];
      const num = o.invoice_number ? `№${o.invoice_number}` : `#${o.invoice_id}`;
      const when = o.expected_date
        ? new Date(o.expected_date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
        : 'раньше';
      return `Плановая ${num} ждала ${when} и не принята — перенесите дату накладной или примите её, товар вернётся сам.`;
    }
    if ((p?.future_stock || []).length) return `Из плановых поставок доступно ${maxPlanned} шт — меньше шага ${step} шт.`;
    return 'Плановых поставок нет.';
  };
  const noStockReason = (isExpired, qty, step) => {
    if (isExpired) return 'Показать нельзя: срок жизни текущей поставки истёк.';
    if (!qty) return 'Показать нельзя: остатка нет.';
    return `Показать нельзя: остаток ${qty} шт меньше шага ${step} шт.`;
  };

  const doSave = async (overrides = {}) => {
    if (flowwowEnabled && !(parseInt(formData.height_cm, 10) > 0)) {
      showToast('Укажите высоту стебля, см — без неё карточка на Flowwow не публикуется', 'error');
      return;
    }
    try {
      const shelfLife = formData.shelf_life_days !== '' ? parseInt(formData.shelf_life_days) : null;
      const data = {
        name: formData.name.trim(),
        short_name: (formData.short_name || '').trim() || null,
        description: formData.description,
        price: parseInt(formData.price, 10),
        step: parseInt(formData.step) || 1,
        category_id: formData.category_id ? parseInt(formData.category_id) : null,
        is_active: formData.is_active,
        // Без категории на витрине не бывает — сохраняем скрытым, что бы ни стояло на тумблере
        is_hidden: formData.category_id ? formData.is_hidden : true,
        // Высота стебля для маркетплейсов (Flowwow); пусто — дефолт из настроек Flowwow
        height_cm: formData.height_cm !== '' && formData.height_cm != null ? parseInt(formData.height_cm, 10) || null : null,
        // Вид цветка: не трогали руками — пусто, сервер сам определит по сохраняемому названию
        // (подсказка могла не успеть за последней правкой названия); вписали — ручная поправка
        flower: flowerAuto ? null : ((formData.flower || '').trim() || null),
        ...overrides,
      };
      // Срок жизни шлём только если он реально менялся: бэк на это поле
      // запускает пересчёт остатка и топап дефицита с причиной «Изменение
      // срока жизни», а при правке одной цены это лишняя работа.
      if (!editingProduct || shelfLife !== (editingProduct.shelf_life_days ?? null)) {
        data.shelf_life_days = shelfLife;
      }

      let productId;

      if (editingProduct) {
        await api.updateProduct(editingProduct.id, data);
        productId = editingProduct.id;
        showToast('Товар обновлён');
      } else {
        data.quantity = 0;
        const newProduct = await api.createProduct(data);
        productId = newProduct.id;
        showToast('Товар создан');
      }

      if (pendingImages.length > 0 && productId) {
        for (const file of pendingImages) {
          try {
            await api.uploadProductImage(productId, file);
          } catch (err) {
            console.error('Image upload error:', err);
          }
        }
        showToast(`Загружено ${pendingImages.length} фото`);
      }

      setShowModal(false);
      setPendingImages([]);
      loadData(archivedMode, { silent: true });
    } catch (error) {
      showToast(error?.message || 'Ошибка сохранения', 'error');
    }
  };

  const handleSubmit = async () => {
    // Обязательные поля
    if (!formData.name.trim()) {
      showToast('Заполните название товара', 'error');
      return;
    }
    const priceNum = parseInt(formData.price, 10);
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      showToast('Укажите цену в рублях (целое число больше 0)', 'error');
      return;
    }

    // Проверка дубликата при создании (как на бэке, чтобы не дёргать зря)
    if (!editingProduct) {
      const key = normProductName(formData.name);
      const dup = namePool.find(p => normProductName(p.name) === key);
      if (dup) {
        if (dup.is_active) showToast('Товар с таким названием уже есть в каталоге', 'error');
        else offerRestoreFromCreate(dup);
        return;
      }
    }

    // Истёк = текущего остатка нет (см. тумблеры и бэк). Без этого сохранение
    // истёкшего товара без плановой поставки молча скрывало его с витрины.
    const qty = editingProduct?.is_expired ? 0 : (parseInt(formData.quantity) || 0);
    const step = parseInt(formData.step) || 1;
    const wantsVisible = !formData.is_hidden && !!formData.category_id;

    // Если редактируем видимый товар, но остатка не хватает на шаг —
    // спрашиваем согласие и сохраняем со скрытием
    if (editingProduct && wantsVisible && qty < step) {
      const future = editingProduct?.future_stock || [];
      const maxPlanned = future.reduce((m, fs) => Math.max(m, fs.available || 0), 0);
      const hasPlannedStock = maxPlanned >= step;
      if (!hasPlannedStock) {
        const plannedLine = plannedStatusLine(editingProduct, maxPlanned, step);
        const reason = editingProduct?.is_expired
          ? 'Срок жизни текущей поставки истёк.'
          : qty ? `Остаток ${qty} шт меньше шага ${step} шт.` : 'Остатка нет.';
        setConfirmDialog({
          title: 'Продавать нечего',
          message: `${reason} ${plannedLine} Товар будет скрыт из каталога. Сохранить?`,
          confirmText: 'Сохранить',
          cancelText: 'Отмена',
          onConfirm: async () => {
            setConfirmDialog(null);
            setFormData(prev => ({ ...prev, is_hidden: true }));
            await doSave({ is_hidden: true });
          },
        });
        return;
      }
    }

    await doSave();
  };

  // Выбор файлов для загрузки
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (editingProduct) {
      // При редактировании — сразу загружаем
      uploadImages(files);
    } else {
      // При создании — добавляем в pending
      setPendingImages(prev => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const uploadImages = async (files) => {
    for (const file of files) {
      try {
        await api.uploadProductImage(editingProduct.id, file);
        showToast('Изображение загружено');
      } catch (error) {
        showToast('Ошибка загрузки', 'error');
      }
    }
    // Обновляем данные
    const updatedProducts = await api.getProducts();
    const productsList = updatedProducts.items || updatedProducts;
    const updated = productsList.find(p => p.id === editingProduct.id);
    if (updated) {
      setEditingProduct(updated);
    }
    loadData();
  };

  const handleImageDelete = async (imageId) => {
    if (!editingProduct) return;
    
    try {
      await api.deleteProductImage(editingProduct.id, imageId);
      showToast('Изображение удалено');
      
      const updatedProducts = await api.getProducts();
      const productsList = updatedProducts.items || updatedProducts;
      const updated = productsList.find(p => p.id === editingProduct.id);
      if (updated) {
        setEditingProduct(updated);
      }
      loadData();
    } catch (error) {
      showToast('Ошибка удаления', 'error');
    }
  };

  const handleImageRotate = async (imageId, direction) => {
    if (!editingProduct) return;

    try {
      // Повёрнутое фото сохраняется под новым именем, старые файлы удаляются
      // (кэш /uploads на месяц) — подменяем адреса в открытой карточке и в списке
      const rotated = await api.rotateProductImage(editingProduct.id, imageId, direction);
      setEditingProduct(prev => prev ? {
        ...prev,
        images: (prev.images || []).map(i => (i.id === rotated.id ? { ...i, ...rotated } : i)),
      } : prev);
      loadData(archivedMode, { silent: true });
    } catch (error) {
      showToast('Ошибка поворота', 'error');
    }
  };

  // Фото одного стебля для Flowwow — отдельный слот, в каталоге не показывается
  const handleFlowwowImageSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editingProduct) return;
    try {
      const updated = await api.uploadProductFlowwowImage(editingProduct.id, file);
      setEditingProduct(prev => prev ? { ...prev, flowwow_image_url: updated.flowwow_image_url, flowwow_image_small: updated.flowwow_image_small } : prev);
      showToast('Фото стебля загружено');
      loadData();
    } catch (error) {
      showToast('Ошибка загрузки', 'error');
    }
  };

  const handleFlowwowImageDelete = async () => {
    if (!editingProduct) return;
    try {
      await api.deleteProductFlowwowImage(editingProduct.id);
      setEditingProduct(prev => prev ? { ...prev, flowwow_image_url: null, flowwow_image_small: null } : prev);
      showToast('Фото стебля удалено');
      loadData();
    } catch (error) {
      showToast('Ошибка удаления', 'error');
    }
  };

  const removePendingImage = (index) => {
    setPendingImages(prev => prev.filter((_, i) => i !== index));
  };

  // Удаление товара
  const handleDeleteProduct = async (product) => {
    if (!(await nfConfirm(`Удалить товар "${product.name}"?`, { confirmText: 'Удалить' }))) {
      return;
    }
    
    try {
      await api.deleteProduct(product.id);
      showToast('Товар удалён');
      loadData();
    } catch (error) {
      showToast('Ошибка удаления', 'error');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedAndFiltered = [...products]
    .filter(p => searchName.trim() ? p.name.toLowerCase().includes(searchName.trim().toLowerCase()) : true)
    .sort((a, b) => {
      if (!sortField) return 0;
      if (sortField === 'price') return sortDir === 'asc' ? a.price - b.price : b.price - a.price;
      if (sortField === 'quantity') return sortDir === 'asc' ? (a.quantity || 0) - (b.quantity || 0) : (b.quantity || 0) - (a.quantity || 0);
      if (sortField === 'physical') return sortDir === 'asc' ? (a.physical_quantity || 0) - (b.physical_quantity || 0) : (b.physical_quantity || 0) - (a.physical_quantity || 0);
      if (sortField === 'loose') {
        const la = a.loose ?? Math.max(0, (a.physical_quantity || 0) - (a.bouquet_locked || 0));
        const lb = b.loose ?? Math.max(0, (b.physical_quantity || 0) - (b.bouquet_locked || 0));
        return sortDir === 'asc' ? la - lb : lb - la;
      }
      if (sortField === 'locked') return sortDir === 'asc' ? (a.bouquet_locked || 0) - (b.bouquet_locked || 0) : (b.bouquet_locked || 0) - (a.bouquet_locked || 0);
      if (sortField === 'name') {
        const aName = a.name.trim();
        const bName = b.name.trim();
        return sortDir === 'asc' ? aName.localeCompare(bName, 'ru') : bName.localeCompare(aName, 'ru');
      }
      if (sortField === 'category') {
        const aVal = a.category?.name || 'я';
        const bVal = b.category?.name || 'я';
        return sortDir === 'asc' ? aVal.localeCompare(bVal, 'ru') : bVal.localeCompare(aVal, 'ru');
      }
      if (sortField === 'hidden') {
        // asc: скрытые сначала, desc: нескрытые сначала
        const aVal = a.is_hidden ? 1 : 0;
        const bVal = b.is_hidden ? 1 : 0;
        return sortDir === 'asc' ? bVal - aVal : aVal - bVal;
      }
      if (sortField === 'future') {
        const aVal = (a.future_stock || []).reduce((s, fs) => s + (fs.available || 0), 0);
        const bVal = (b.future_stock || []).reduce((s, fs) => s + (fs.available || 0), 0);
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });

  const sortIcon = (field) => {
    const inactive = sortField !== field;
    const dir = sortDir === 'asc' ? 'asc' : 'desc';
    return (
      <svg
        width="11" height="11" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ marginLeft: 4, verticalAlign: 'middle', opacity: inactive ? 0.35 : 1 }}
      >
        {inactive ? (
          <>
            <polyline points="7 15 12 20 17 15"/>
            <polyline points="7 9 12 4 17 9"/>
          </>
        ) : dir === 'asc' ? (
          <polyline points="7 14 12 9 17 14"/>
        ) : (
          <polyline points="7 10 12 15 17 10"/>
        )}
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  // Стиль табов в стиле раздела «Склад» — текстовый таб с подчёркиванием + иконкой

  // Иконки табов — line-style SVG (наследуют currentColor)
  const ICONS = {
    products: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7l9-4 9 4-9 4-9-4z"/>
        <path d="M3 7v10l9 4 9-4V7"/>
        <path d="M12 11v10"/>
      </svg>
    ),
    subscriptions: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-7.66-4.27"/>
        <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 7.66 4.27"/>
        <polyline points="21 4 21 9 16 9"/>
        <polyline points="3 20 3 15 8 15"/>
      </svg>
    ),
    archive: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="5" rx="1"/>
        <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8"/>
        <line x1="10" y1="13" x2="14" y2="13"/>
      </svg>
    ),
    packaging: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 9h14l-2 10H7L5 9z"/>
        <path d="M7 9l2-3h6l2 3"/>
        <path d="M12 6v13"/>
      </svg>
    ),
    bouquets: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8" cy="7" r="2.2"/>
        <circle cx="12" cy="5" r="2.2"/>
        <circle cx="16" cy="7" r="2.2"/>
        <path d="M8 9.2 L11 15"/>
        <path d="M12 7.2 L12 15"/>
        <path d="M16 9.2 L13 15"/>
        <path d="M9 15 L15 15 L13.5 21 L10.5 21 Z"/>
      </svg>
    ),
    categories: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
        <circle cx="7" cy="7" r="1"/>
      </svg>
    ),
  };

  const renderTabs = ({ addButton } = {}) => (
    <div className="tabs" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
      <button
        className={`tab${activeTab === 'products' && !archivedMode ? ' active' : ''}`}
        onClick={() => { setActiveTab('products'); setArchivedMode(false); setSearchName(''); }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {ICONS.products}
        Товары
        {renderLabelPencil('products')}
      </button>
      <button
        className={`tab${activeTab === 'bouquets' && !archivedMode ? ' active' : ''}`}
        onClick={() => { setActiveTab('bouquets'); setArchivedMode(false); setSearchName(''); }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {ICONS.bouquets}
        Букеты
        {renderLabelPencil('bouquets')}
      </button>
      <button
        className={`tab${activeTab === 'subscriptions' ? ' active' : ''}`}
        onClick={() => { setActiveTab('subscriptions'); setArchivedMode(false); setSearchName(''); }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {ICONS.subscriptions}
        Подписки
        {renderLabelPencil('subscription')}
      </button>
      <button
        className={`tab${activeTab === 'categories' ? ' active' : ''}`}
        onClick={() => { setActiveTab('categories'); setArchivedMode(false); setSearchName(''); }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {ICONS.categories}
        Категории
      </button>
      <button
        className={`tab${activeTab === 'packaging' ? ' active' : ''}`}
        onClick={() => { setActiveTab('packaging'); setArchivedMode(false); setSearchName(''); }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {ICONS.packaging}
        Упаковка
      </button>
      <button
        className={`tab${archivedMode ? ' active' : ''}`}
        onClick={() => {
          setArchivedMode(true);
          // Архив для букетов и стеблей раздельный — оставляем выбранный каталог.
          // Подписки/упаковка/категории не имеют отдельного архива, перекидываем на товары.
          if (activeTab !== 'products' && activeTab !== 'bouquets') {
            setActiveTab('products');
          }
          setSearchName('');
        }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {ICONS.archive}
        Архив
      </button>
      {addButton && (
        <button
          type="button"
          className="btn btn-primary btn-md"
          onClick={addButton.onClick}
          style={{ marginLeft: 'auto', marginBottom: 4 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          {addButton.label.replace(/^\+\s*/, '')}
        </button>
      )}

      {/* Владелец: переименование раздела витрины (модалка живёт в renderTabs,
          т.к. табы рендерятся во всех ветках страницы) */}
      {labelEditor && (
        <Modal
          title="Название раздела на витрине"
          onClose={() => setLabelEditor(null)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setLabelEditor(null)}>
                Отмена
              </button>
              <button className="btn btn-primary" onClick={saveCatalogLabel}>
                Сохранить
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">
              Как раздел называется у покупателей (таб на витрине)
            </label>
            <input
              type="text"
              className="form-input"
              maxLength={30}
              value={labelEditor.value}
              onChange={(e) => setLabelEditor((prev) => ({ ...prev, value: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') saveCatalogLabel(); }}
              autoFocus
            />
            <small style={{ display: 'block', marginTop: 6, color: '#666', fontSize: 12 }}>
              Применяется на витрине сразу, без пересборки приложения.
            </small>
          </div>
        </Modal>
      )}
    </div>
  );


  if (activeTab === 'subscriptions') {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Каталог</h1>
        </div>
        {renderTabs({ addButton: { label: '+ Добавить подписку', onClick: () => setSubscriptionTabAction('create') } })}
        <SubscriptionCatalogTab showToast={showToast} createTrigger={subscriptionTabAction} onCreateHandled={() => setSubscriptionTabAction(null)} />
      </div>
    );
  }

  if (activeTab === 'bouquets') {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">{archivedMode ? 'Архив букетов' : 'Каталог'}</h1>
        </div>
        {renderTabs(!archivedMode ? { addButton: { label: '+ Добавить букет', onClick: () => setBouquetsCreateTrigger(Date.now()) } } : {})}
        <BouquetsPage
          showToast={showToast}
          embedded
          archivedMode={archivedMode}
          createTrigger={bouquetsCreateTrigger}
          onCreateHandled={() => setBouquetsCreateTrigger(null)}
        />
      </div>
    );
  }

  if (activeTab === 'categories') {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Каталог</h1>
        </div>
        {renderTabs()}
        <CategoriesTab showToast={showToast} />
      </div>
    );
  }

  if (activeTab === 'packaging') {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Каталог</h1>
        </div>
        {renderTabs({ addButton: { label: '+ Добавить упаковку', onClick: () => setPackagingCreateTrigger(Date.now()) } })}
        <PackagingPage
          showToast={showToast}
          embedded
          createTrigger={packagingCreateTrigger}
          onCreateHandled={() => setPackagingCreateTrigger(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          {archivedMode ? 'Архив' : 'Каталог'} {totalProducts > 0 && <span style={{ fontSize: 22, fontWeight: 500, color: '#5E6561' }}>({totalProducts})</span>}
        </h1>
      </div>

      {renderTabs(!archivedMode ? { addButton: { label: '+ Добавить товар', onClick: openCreateModal } } : {})}

      {(
        <div className="table-container">
          {/* Раскладка фиксированная: при авто-раскладке ширины колонок пересчитывались
              под видимые строки, и при вводе в поиск названия переносились по-разному —
              строки «ездили» (30.09.2026). Ширины — по заголовкам и данным при окне 1440;
              «Название» забирает остаток, на широком экране — всё свободное место. */}
          <table className="table" style={{ tableLayout: 'fixed', minWidth: 1100 }}>
            <colgroup>
              <col style={{ width: 78 }} />
              <col />
              <col style={{ width: 119 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 78 }} />
              <col style={{ width: 103 }} />
              <col style={{ width: 103 }} />
              <col style={{ width: 102 }} />
              <col style={{ width: 138 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 84 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Фото</th>
                <th style={{ userSelect: 'none', position: 'relative' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, visibility: (searchInHeader || searchName) ? 'hidden' : 'visible' }}>
                    <span style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                      Название{sortIcon('name')}
                    </span>
                    <button
                      onClick={() => setSearchInHeader(true)}
                      title="Поиск по названию"
                      style={{
                        width: 28, height: 28, padding: 0, border: 'none',
                        background: 'transparent', cursor: 'pointer',
                        color: '#1C3D2E', display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center',
                        '--ch': '5px', transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(28,61,46,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    </button>
                  </div>
                  {(searchInHeader || searchName) && (
                    <div style={{ position: 'absolute', left: 12, right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                      <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="#1C3D2E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }}
                      >
                        <circle cx="11" cy="11" r="7"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      <input
                        autoFocus
                        type="text"
                        placeholder="Поиск по названию"
                        value={searchName}
                        onChange={e => setSearchName(e.target.value)}
                        onBlur={() => { if (!searchName) setSearchInHeader(false); }}
                        style={{
                          padding: '6px 26px 6px 30px',
                          border: '1px solid #1C3D2E', '--ch': '6px', '--stroke': '#1C3D2E',
                          fontSize: 13, fontWeight: 500, width: '100%', boxSizing: 'border-box', outline: 'none',
                          background: '#fff', color: '#1C3D2E',
                          textTransform: 'none', letterSpacing: '0.01em',
                        }}
                      />
                      {searchName && (
                        <button
                          onClick={() => { setSearchName(''); setSearchInHeader(false); }}
                          style={{
                            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                            width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%',
                            cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          title="Очистить"
                        >×</button>
                      )}
                    </div>
                  )}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('category')}>
                  Категория{sortIcon('category')}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('price')}>
                  Цена{sortIcon('price')}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('physical')}>
                  Всего{sortIcon('physical')}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('loose')}>
                  Стеблями{sortIcon('loose')}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('locked')}>
                  В букетах{sortIcon('locked')}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('quantity')}>
                  Доступно{sortIcon('quantity')}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('future')}>
                  Поставки{sortIcon('future')}
                </th>
                <th>Шаг</th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'center' }}
                  onClick={() => handleSort('hidden')}
                  title="Сортировка: скрытые сначала / нескрытые сначала"
                >
                  Виден{sortIcon('hidden')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFiltered.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '40px 20px', color: '#5E6561' }}>
                    {searchName ? 'Ничего не найдено' : 'Товаров пока нет'}
                  </td>
                </tr>
              )}
              {sortedAndFiltered.map((product, index) => {
                const dimStyle = product.is_hidden ? { opacity: 0.5 } : null;
                return (
                <tr
                  key={product.id}
                  onClick={() => openEditModal(product)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={dimStyle} onClick={e => {
                    if (!product.images?.length) return;
                    e.stopPropagation();
                    setLightbox({
                      images: product.images.map(img => ({ src: `${API_URL}${img.image_url}` })),
                      index: 0,
                    });
                  }}>
                    {product.images?.length > 0 ? (
                      <img
                        src={`${API_URL}${product.images[0].image_url}`}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        style={{ width: 50, height: 50, objectFit: 'cover', '--ch': '6px', cursor: 'zoom-in' }}
                      />
                    ) : (
                      <div style={{
                        width: 50,
                        height: 50,
                        background: '#F4F4EF',
                        '--ch': '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        color: '#5E6561'
                      }}>
                        📷
                      </div>
                    )}
                  </td>
                  {/* Длинное название без пробелов переносим внутри слова — иначе налезает на цену */}
                  <td style={{ ...dimStyle, overflowWrap: 'anywhere', paddingRight: 12 }}><strong>{product.name}</strong></td>
                  <td style={{ ...dimStyle, color: '#5E6561' }}>{product.category?.name || '—'}</td>
                  <td style={{ ...dimStyle, fontWeight: 700, fontSize: 15, color: '#1C3D2E', whiteSpace: 'nowrap' }}>
                    {product.price.toLocaleString('ru-RU')} ₽
                  </td>
                  <td style={{ ...dimStyle, fontWeight: 700, fontSize: 15, color: (product.physical_quantity || 0) > 0 ? '#1C3D2E' : '#C1C8C4' }}
                      title="Всего физически (стебли + в букетах)">
                    {(product.physical_quantity || 0) > 0 ? product.physical_quantity : '—'}
                  </td>
                  {(() => {
                    const loose = product.loose ?? Math.max(0, (product.physical_quantity || 0) - (product.bouquet_locked || 0));
                    const locked = product.bouquet_locked || 0;
                    return (
                      <>
                        <td style={{ ...dimStyle, fontWeight: 700, fontSize: 15, color: loose > 0 ? '#1C3D2E' : '#C1C8C4' }}
                            title="Свободные стебли (физически на складе, не в букетах)">
                          {loose > 0 ? loose : '—'}
                        </td>
                        <td style={{ ...dimStyle, fontWeight: 700, fontSize: 15, color: locked > 0 ? '#7E5D00' : '#C1C8C4' }}
                            title="Залочено в собранных букетах">
                          {locked > 0 ? locked : '—'}
                        </td>
                      </>
                    );
                  })()}
                  <td style={dimStyle}>
                    {(() => {
                      const qty = product.quantity || 0;
                      const step = product.step || 1;
                      let daysLeft = null;
                      if (product.stock_arrival_date && product.shelf_life_days != null) {
                        const arrival = new Date(product.stock_arrival_date + 'T00:00:00');
                        const expires = new Date(arrival.getTime() + product.shelf_life_days * 86400000);
                        const today = new Date(); today.setHours(0,0,0,0);
                        daysLeft = Math.ceil((expires - today) / 86400000);
                      }
                      const tooltipText = product.stock_arrival_date
                        ? [
                            `Поступление: ${new Date(product.stock_arrival_date + 'T00:00:00').toLocaleDateString('ru-RU')}`,
                            product.shelf_life_days != null ? `Срок жизни: ${product.shelf_life_days} дн` : 'Срок жизни: бессрочно',
                            daysLeft != null ? `Осталось: ${daysLeft} дн` : null,
                          ].filter(Boolean).join('\n')
                        : '';
                      const content = (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
                          <span style={{
                            fontWeight: 700, fontSize: 15,
                            color: product.is_expired ? '#999' : (qty === 0 ? '#B0603C' : (qty < step ? '#B0603C' : '#1C3D2E')),
                          }}>{qty}</span>
                          {product.is_expired ? (
                            <span style={{
                              background: '#FFE7DD', color: '#B0603C',
                              '--ch': '5px', padding: '2px 8px', fontSize: 11, fontWeight: 600,
                            }}>истёк</span>
                          ) : (daysLeft != null && qty > 0) && (
                            <span style={{
                              fontSize: 11, fontWeight: 600,
                              '--ch': '5px', padding: '2px 8px',
                              ...(daysLeft <= 3
                                ? { color: '#7E5D00', background: '#FEF6E6' }
                                : { color: '#1C3D2E', background: '#DFF2E8' }),
                            }}>{daysLeft === 0 ? 'сегодня' : `${daysLeft} дн`}</span>
                          )}
                        </div>
                      );
                      return tooltipText ? <Tooltip text={tooltipText}>{content}</Tooltip> : content;
                    })()}
                  </td>
                  <td style={dimStyle}>
                    {product.future_stock?.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* Показываем только 2 ближайшие плановые поставки (визуальное ограничение).
                            Если их больше — добавляем «ещё N» с tooltip по остальным датам. */}
                        {product.future_stock.slice(0, 2).map((fs, i) => {
                          const step = product.step || 1;
                          const total = fs.total ?? fs.available;
                          const free = fs.available || 0;
                          const openDateStr = new Date(fs.date + 'T00:00:00')
                            .toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '');
                          let bg, color;
                          if (free === 0) { bg = '#F4F4EF'; color = '#9A9A95'; }
                          else if (free < step) { bg = '#FEF6E6'; color = '#7E5D00'; }
                          else { bg = '#DFF2E8'; color = '#1C3D2E'; }
                          const lines = [];
                          if (fs.supplier_name) lines.push(`Поставщик: ${fs.supplier_name}`);
                          if (fs.invoice_number) lines.push(`Накладная № ${fs.invoice_number}`);
                          if (fs.invoice_date) lines.push(`Дата накладной: ${new Date(fs.invoice_date + 'T00:00:00').toLocaleDateString('ru-RU')}`);
                          lines.push(`Открытие продаж: ${new Date(fs.date + 'T00:00:00').toLocaleDateString('ru-RU')}${fs.extra_days ? ` (буфер ${fs.extra_days} дн)` : ''}`);
                          lines.push(`Забронировано: ${fs.reserved || 0} из ${total}`);
                          const tooltipText = lines.join('\n');
                          return (
                            <Tooltip key={i} text={tooltipText}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'baseline', gap: '0 6px',
                                flexWrap: 'wrap', maxWidth: '100%', boxSizing: 'border-box',
                                padding: '3px 8px', '--ch': '5px',
                                background: bg, color, fontSize: 13, fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}>
                                <span>{openDateStr} <span style={{ opacity: 0.5 }}>·</span></span>
                                <span>{free}/{total}</span>
                              </span>
                            </Tooltip>
                          );
                        })}
                        {product.future_stock.length > 2 && (() => {
                          const rest = product.future_stock.slice(2);
                          const tooltipText = rest.map(fs => {
                            const d = new Date(fs.date + 'T00:00:00').toLocaleDateString('ru-RU');
                            const free = fs.available || 0;
                            const total = fs.total ?? fs.available;
                            return `${d} · ${free}/${total}`;
                          }).join('\n');
                          return (
                            <Tooltip text={tooltipText}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'baseline', gap: 4,
                                padding: '2px 8px', '--ch': '5px',
                                background: '#F4F4EF', color: '#5E6561', fontSize: 12, fontWeight: 500,
                                whiteSpace: 'nowrap', cursor: 'help',
                              }}>
                                ещё {rest.length}
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </div>
                    ) : (
                      <span style={{ color: '#C1C8C4', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ ...dimStyle, whiteSpace: 'nowrap', minWidth: 70, color: '#1C3D2E', fontWeight: 700, fontSize: 15 }}>
                    {product.step || 1} шт
                  </td>
                  <td style={{ ...dimStyle, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <Toggle
                      checked={!product.is_hidden}
                      onChange={async (wantsVisible) => {
                        if (wantsVisible && !product.category) {
                          showToast('У товара нет категории — на витрине он не показывается. Укажите категорию в карточке', 'error');
                          return;
                        }
                        const step = product.step || 1;
                        // Истёкший срок обнуляет текущий остаток, но не запрещает
                        // показ: под плановую поставку карточка живёт на витрине
                        // с «доступно с …» (так же считает и бэк при сохранении).
                        const qty = product.is_expired ? 0 : (product.quantity || 0);
                        if (wantsVisible && qty < step) {
                          const future = product.future_stock || [];
                          const maxPlanned = future.reduce((m, fs) => Math.max(m, fs.available || 0), 0);
                          if (maxPlanned < step) {
                            showToast(`${noStockReason(product.is_expired, qty, step)} ${plannedStatusLine(product, maxPlanned, step)}`, 'error');
                            return;
                          }
                        }
                        const newHidden = !wantsVisible;
                        try {
                          await api.updateProduct(product.id, { is_hidden: newHidden });
                          setProducts(prev => prev.map(p =>
                            p.id === product.id ? { ...p, is_hidden: newHidden } : p
                          ));
                        } catch (error) {
                          showToast(error?.message || 'Ошибка обновления', 'error');
                        }
                      }}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={editingProduct ? (editingProduct.name || 'Без названия') : 'Новый товар'}
          onClose={() => { setShowModal(false); setPendingImages([]); setRestoredId(null); }}
          confirmOverlayClose
          footer={
            <>
              {editingProduct && (
                <button
                  className={archivedMode ? 'btn btn-secondary' : 'btn btn-danger-outline'}
                  onClick={() => {
                    setShowModal(false);
                    setPendingImages([]);
                    handleArchive(editingProduct);
                  }}
                  style={{ marginRight: 'auto' }}
                >
                  {archivedMode ? 'Восстановить' : 'В архив'}
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => { setShowModal(false); setPendingImages([]); setRestoredId(null); }}>
                Отмена
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!formData.name.trim() || !formData.price || parseInt(formData.price, 10) <= 0}
                title={!formData.name.trim() || !formData.price || parseInt(formData.price, 10) <= 0 ? 'Заполните название и цену' : undefined}
              >
                {editingProduct ? 'Сохранить' : 'Создать'}
              </button>
            </>
          }
        >
          {/* Только что вернули из архива — видно, что сработало (тост быстро исчезает,
              а карточка выглядит как обычная правка) */}
          {editingProduct && restoredId === editingProduct.id && (
            <div style={{
              padding: '10px 14px', marginBottom: 12, background: '#DFF2E8', color: '#1C3D2E',
              border: '1px solid #1C3D2E', '--ch': '8px', '--stroke': '#1C3D2E', fontSize: 13,
            }}>
              <strong>✓ Карточка восстановлена из архива</strong> — она снова в каталоге товаров.
              Пока скрыта с витрины: включите показ, выберите категорию и проверьте цену, когда будет остаток.
            </div>
          )}

          {/* Видимость — тумблер сверху */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 14px', background: '#F4F4EF', '--ch': '8px', '--stroke': '#D8DDDA',
            border: '1px solid #D8DDDA', marginBottom: 14,
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1C3D2E' }}>
                {formData.is_hidden ? 'Скрыт из каталога' : 'Виден в каталоге'}
              </div>
              <div style={{ fontSize: 12, color: '#5E6561', marginTop: 2 }}>
                {!formData.category_id
                  ? 'Без категории на витрине не показывается — выберите категорию ниже'
                  : formData.is_hidden ? 'Товар не показывается клиентам' : 'Товар доступен в приложении'}
              </div>
            </div>
            <Toggle
              checked={!formData.is_hidden}
              onChange={(wantsVisible) => {
                if (wantsVisible) {
                  if (!formData.category_id) {
                    showToast('У товара нет категории — на витрине он не показывается. Выберите категорию ниже', 'error');
                    return;
                  }
                  // Истёк = текущего остатка нет; плановая поставка по-прежнему
                  // даёт право показывать карточку (см. тумблер в списке).
                  const qty = editingProduct?.is_expired ? 0 : (parseInt(formData.quantity) || 0);
                  const step = parseInt(formData.step) || 1;
                  const future = editingProduct?.future_stock || [];
                  const maxPlanned = future.reduce((m, fs) => Math.max(m, fs.available || 0), 0);
                  if (qty < step && maxPlanned < step) {
                    showToast(`${noStockReason(editingProduct?.is_expired, qty, step)} ${plannedStatusLine(editingProduct, maxPlanned, step)}`, 'error');
                    return;
                  }
                }
                setFormData(prev => ({ ...prev, is_hidden: !wantsVisible }));
              }}
            />
          </div>

          {/* Фотографии — компактная горизонтальная лента */}
          <div className="form-group">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* Загруженные (для editing) */}
              {editingProduct?.images?.map((img, idx) => (
                <div key={img.id} style={{ position: 'relative', width: 100, height: 100 }}>
                  <img
                    src={`${API_URL}${img.image_url}?t=${Date.now()}`}
                    alt=""
                    style={{ width: 100, height: 100, objectFit: 'cover', '--ch': '8px', cursor: 'zoom-in' }}
                    onClick={() => setModalLightboxIndex(idx)}
                  />
                  {/* «×» на фото — удаление, красным, не терракотой (UX-аудит 25.10) */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleImageDelete(img.id); }}
                    title="Удалить"
                    style={{
                      position: 'absolute', top: 6, right: 6,
                      width: 22, height: 22, borderRadius: '50%', border: 'none',
                      background: 'rgba(203,77,69, 0.95)', color: '#fff', cursor: 'pointer',
                      fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)', zIndex: 2,
                    }}
                  >×</button>
                </div>
              ))}
              {/* Pending (для new) */}
              {!editingProduct && pendingImages.map((file, index) => (
                <div key={`p-${index}`} style={{ position: 'relative', width: 100, height: 100 }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt=""
                    style={{ width: 100, height: 100, objectFit: 'cover', '--ch': '8px'}}
                  />
                  <button
                    type="button"
                    onClick={() => removePendingImage(index)}
                    title="Удалить"
                    style={{
                      position: 'absolute', top: 6, right: 6,
                      width: 22, height: 22, borderRadius: '50%', border: 'none',
                      background: 'rgba(203,77,69, 0.95)', color: '#fff', cursor: 'pointer',
                      fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)', zIndex: 2,
                    }}
                  >×</button>
                </div>
              ))}
              {/* "+" tile */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: 100, height: 100, border: '2px dashed #D8DDDA', '--ch': '8px', '--stroke': '#D8DDDA', '--bw': '2px',
                  background: '#fff', cursor: 'pointer', fontSize: 32, color: '#1C3D2E',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                }}
                title="Загрузить фото"
              >+</button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              {/* Фото одного стебля для Flowwow — главное фото карточки на площадке, в каталоге не показывается */}
              {flowwowEnabled && (
                <div
                  style={{ position: 'relative', width: 100, height: 100, marginLeft: 8 }}
                  title={!editingProduct
                    ? 'Фото одного стебля для Flowwow — загрузите после сохранения товара'
                    : editingProduct.flowwow_image_url
                      ? 'Главное фото на Flowwow, остальные фото идут следом. В каталоге не показывается'
                      : 'Без фото одного стебля товар не уйдёт на Flowwow'}
                >
                  {editingProduct?.flowwow_image_url ? (
                    <>
                      <img
                        src={`${API_URL}${editingProduct.flowwow_image_small || editingProduct.flowwow_image_url}`}
                        alt=""
                        style={{ width: 100, height: 100, objectFit: 'cover', '--ch': '8px' }}
                      />
                      <span style={{
                        position: 'absolute', left: 0, right: 0, bottom: 0, padding: '2px 0',
                        background: 'rgba(194, 24, 91, 0.85)', color: '#fff', fontSize: 10, fontWeight: 700,
                        textAlign: 'center', letterSpacing: '0.02em',
                      }}>Flowwow</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleFlowwowImageDelete(); }}
                        title="Удалить"
                        style={{
                          position: 'absolute', top: 6, right: 6,
                          width: 22, height: 22, borderRadius: '50%', border: 'none',
                          background: 'rgba(203,77,69, 0.95)', color: '#fff', cursor: 'pointer',
                          fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)', zIndex: 2,
                        }}
                      >×</button>
                    </>
                  ) : (
                    <button
                      type="button"
                      disabled={!editingProduct}
                      onClick={() => flowwowFileInputRef.current?.click()}
                      style={{
                        width: 100, height: 100, border: '2px dashed #F0B3C8', '--ch': '8px', '--stroke': '#F0B3C8', '--bw': '2px',
                        background: '#fff', cursor: editingProduct ? 'pointer' : 'default', color: '#C2185B',
                        opacity: editingProduct ? 1 : 0.6,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                        lineHeight: 1.2, fontSize: 11, fontWeight: 600, textAlign: 'center', padding: 4,
                      }}
                    >
                      <span style={{ fontSize: 26, lineHeight: 1, fontWeight: 400 }}>+</span>
                      стебель для<br />Flowwow
                    </button>
                  )}
                  <input
                    ref={flowwowFileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFlowwowImageSelect}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Название + Цена в одной строке */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1, position: 'relative' }}>
              <label className="form-label">Название <span style={{ color: '#B0603C' }}>*</span></label>
              <input
                type="text"
                className="form-input"
                placeholder="Название товара"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                onFocus={() => setNameFocused(true)}
                onBlur={() => setNameFocused(false)}
              />
              {!editingProduct && nameFocused && formData.name.trim().length >= 2 && (() => {
                const q = normProductName(formData.name);
                const matches = namePool
                  .filter(p => normProductName(p.name).includes(q))
                  .slice(0, 6);
                const exact = namePool.find(p => normProductName(p.name) === q);
                if (matches.length === 0) return null;
                return (
                  <div style={{
                    marginTop: 6, padding: 8,
                    background: '#F4F4EF', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA',
                  }}>
                    {exact && (
                      <div style={{
                        padding: '6px 8px', marginBottom: 4,
                        background: '#FFE7DD', color: '#A85F58', '--ch': '5px',
                        fontSize: 12, fontWeight: 600,
                      }}>
                        {exact.is_active
                          ? 'Такое название уже используется — создать нельзя'
                          : 'Такое название есть в архиве — нажмите на него, чтобы восстановить'}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#5E6561', textTransform: 'uppercase', letterSpacing: 0.4, padding: '2px 4px' }}>
                      Похожие товары:
                    </div>
                    {/* Клик: архив → «Восстановить?», каталог → открыть карточку. onMouseDown —
                        иначе blur поля прячет список раньше клика */}
                    {matches.map((p, i) => (
                      <div key={p.id ?? i}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (p.is_active) { setShowModal(false); openEditModal(p); }
                          else offerRestoreFromCreate(p);
                        }}
                        title={p.is_active ? 'Открыть карточку' : 'Восстановить из архива'}
                        style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '6px 8px', fontSize: 13, color: '#1C3D2E', cursor: 'pointer',
                      }}>
                        <span>{p.name}</span>
                        <span style={{
                          fontSize: 11, fontWeight: 600,
                          padding: '2px 8px', '--ch': '5px',
                          background: p.is_active ? '#DFF2E8' : '#F4F4EF',
                          color: p.is_active ? '#1C3D2E' : '#5E6561',
                        }}>
                          {p.is_active ? 'Каталог · открыть' : 'Архив · восстановить'}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="form-group" style={{ width: 130 }}>
              <label className="form-label">Цена (₽) <span style={{ color: '#B0603C' }}>*</span></label>
              <input
                type="number"
                className="form-input"
                placeholder="0"
                min="1"
                step="1"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value.replace(/[^\d]/g, '') }))}
              />
            </div>
          </div>

          {/* Сокращённое название (состав букета на витрине) + высота стебля (Flowwow) в одну строку */}
          <div style={{ display: 'flex', gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Сокращённое название</label>
            <input
              type="text"
              className="form-input"
              placeholder="Для состава букета, напр. «Гвоз. желтая»"
              maxLength={12}
              value={formData.short_name}
              onChange={(e) => setFormData(prev => ({ ...prev, short_name: e.target.value.slice(0, 12) }))}
            />
            <div style={{ textAlign: 'right', fontSize: 11, color: (formData.short_name || '').length >= 12 ? '#B0603C' : '#999' }}>
              {(formData.short_name || '').length}/12
            </div>
          </div>
          <div className="form-group" style={{ width: 130 }}
               title={flowwowEnabled ? 'Высота стебля — обязательна для карточки на Flowwow' : 'Высота стебля — для маркетплейсов (Flowwow)'}>
            <label className="form-label">Высота, см{flowwowEnabled && <span style={{ color: '#CB4D45' }}> *</span>}</label>
            <input
              type="number"
              className="form-input"
              placeholder="—"
              min="1"
              required={flowwowEnabled}
              value={formData.height_cm}
              onChange={(e) => setFormData(prev => ({ ...prev, height_cm: e.target.value }))}
            />
          </div>
          </div>

          {/* Вид цветка: подборки рекламы и «Есть сегодня» на витрине собираются по нему.
              Само ставится по названию; поправить — если не угадало («Мандарин» → роза) */}
          <div className="form-group">
            <label className="form-label">Цветок</label>
            <input
              type="text"
              className="form-input"
              list="product-flower-options"
              placeholder="роза, гвоздика, калла…"
              maxLength={40}
              value={formData.flower}
              onChange={(e) => { setFlowerAuto(false); setFormData(prev => ({ ...prev, flower: e.target.value })); }}
            />
            <datalist id="product-flower-options">
              {flowerHint.flowers.map(f => <option key={f} value={f} />)}
            </datalist>
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {flowerAuto
                ? (formData.flower ? 'Определили по названию — поправьте, если неверно' : 'Не определили по названию — впишите вид цветка')
                : (flowerHint.guess && flowerHint.guess !== (formData.flower || '').trim().toLowerCase()
                  ? `По названию было бы: ${flowerHint.guess}`
                  : 'Вид цветка для подборок и похожих товаров на витрине')}
            </div>
          </div>

          {/* Шаг продажи: переключатель + произвольное значение (UX-аудит 25.10) */}
          <div className="form-group">
            <label className="form-label">Шаг продажи</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="seg">
                {STEP_PRESETS.map(s => {
                  const isActive = customStep === '' && parseInt(formData.step) === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      className={`seg-btn${isActive ? ' on' : ''}`}
                      onClick={() => { setCustomStep(''); setFormData(prev => ({ ...prev, step: s.toString() })); }}
                      style={{ minWidth: 56 }}
                    >
                      {s} шт
                    </button>
                  );
                })}
              </div>
              <input
                type="number"
                min="1"
                placeholder="свой"
                value={customStep}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d]/g, '');
                  setCustomStep(v);
                  // Стёрли своё значение — возвращаемся к шагу 1
                  setFormData(prev => ({ ...prev, step: v || '1' }));
                }}
                style={{
                  width: 72, padding: '6px 10px', '--ch': '6px', fontSize: 13, outline: 'none', marginLeft: 4,
                  fontWeight: customStep !== '' ? 600 : 400,
                  border: `1px solid ${customStep !== '' ? '#1C3D2E' : '#D8DDDA'}`,
                  '--stroke': customStep !== '' ? '#1C3D2E' : '#D8DDDA',
                  background: customStep !== '' ? '#1C3D2E' : '#fff',
                  color: customStep !== '' ? '#fff' : '#1C3D2E',
                }}
              />
              <span style={{ color: '#5E6561', fontSize: 13 }}>шт</span>
            </div>
          </div>

          {/* Категория + Срок жизни */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Категория</label>
              <Select
                field
                value={formData.category_id}
                onChange={(v) => setFormData(prev => ({ ...prev, category_id: v === '' || v == null ? '' : String(v) }))}
                placeholder="Без категории"
                options={[
                  { value: '', label: 'Без категории' },
                  // Только категории стеблей; букетная — лишь если товар уже в ней (видно и можно перевести)
                  ...categories
                    .filter(cat => cat.scope !== 'bouquets' || String(cat.id) === String(formData.category_id))
                    .map(cat => ({ value: String(cat.id), label: cat.scope === 'bouquets' ? `${cat.name} (категория букетов)` : cat.name })),
                ]}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Срок жизни</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  className="form-input"
                  placeholder="бессрочно"
                  min="0"
                  value={formData.shelf_life_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, shelf_life_days: e.target.value }))}
                  style={{ width: '100%' }}
                />
                <span style={{ color: '#5E6561', fontSize: 13, whiteSpace: 'nowrap' }}>дней</span>
              </div>
              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                Пусто — товар не портится и с витрины по сроку не уходит. Для цветов укажите дни.
              </div>
            </div>
          </div>

          {/* Описание */}
          <div className="form-group">
            <label className="form-label">Описание</label>
            <textarea
              className="form-input"
              placeholder="Описание товара"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              style={{ minHeight: 60, resize: 'vertical' }}
            />
            {/* Как у букетов: описание стебля уходит в карточку Flowwow (flowwow_catalog), на витрине его нет */}
            {flowwowEnabled && (
              <small style={{ display: 'block', marginTop: 4, color: '#5E6561', fontSize: 11 }}>
                На витрине не показывается — уходит на Flowwow.
              </small>
            )}
          </div>

        </Modal>
      )}

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {lightbox && (
        <PhotoLightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}

      {modalLightboxIndex !== null && editingProduct?.images?.length > 0 && (
        <PhotoLightbox
          images={editingProduct.images.map(i => ({ src: `${API_URL}${i.image_url}?t=${Date.now()}`, id: i.id }))}
          startIndex={modalLightboxIndex}
          onClose={() => setModalLightboxIndex(null)}
          onRotate={(image, dir) => handleImageRotate(image.id, dir)}
        />
      )}
    </div>
  );
}

export default ProductsPage;
